import { createSlice } from "@reduxjs/toolkit";
import { graphqlApi } from "../api";
import { searchOptions } from "../constants";
import { STATUSES } from "../../../util/constants";
import { selectCategorizedResults } from "./buckeye-link";
import { getBuckeyeLinkData } from "../../data-sources/enums";
import { searchResultOptions } from "../../../Search/util/functions";

const initialState = {
  query: "",
  cache: [],
  status: STATUSES.IDLE,
  results: []
};


const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    clear(state) {
      if(state.query) {
        state.query = ""
      }
    },
    setSearchResultData(state, action) {
      const { payload } = action
      state.status = STATUSES.SUCCESS
      Object.entries(payload).forEach(([query, value]) => {
        if(query) {
          state[query] = value ?? []
        }
      })
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      graphqlApi.endpoints.getSearchResults.matchFulfilled,
      (state, searchResults) => {
        state.status = STATUSES.SUCCESS;
        const queryString = searchResults?.queryString 
          ?? searchResults?.meta?.arg?.originalArgs?.queryString
        
        if(queryString) {
          let match = state.cache.find(({ query }) => {
            return query === queryString
          }) 
          if(!match) {
            state.cache.push({
              query: queryString,
              payload: searchResults?.payload
            })
          } else {
            match.payload = searchResults?.payload
          }
        }
      }

    );
    builder.addMatcher(
      graphqlApi.endpoints.getSearchResults.matchPending,
      (state) => {
        state.status = STATUSES.LOADING;
      }
    );
    builder.addMatcher(
      graphqlApi.endpoints.getSearchResults.matchRejected,
      (state) => {
        state.status = STATUSES.ERROR;
      }
    );
  },
});

export const { clear, setQuery, setSearchResultData } = searchSlice.actions
const reducers = {
  getSearchResults: searchSlice.reducer,
};

export const selectAboutYouSearchResults = (state = {}, query) => { 
  if(!query) {
    return undefined
  }
  const cache = state?.search?.cache ?? []
  if(!cache?.length) {
    return undefined
  }
  
  return cache.find(({ query: q }) => q === query)?.payload?.[searchOptions.getPersonal]
};

export const selectSearchResults = (state = {}, query) => { 
  if(!query) {
    return undefined
  }
  const cache = state?.search?.cache ?? []
  if(!cache?.length) {
    return undefined
  }

  return cache.find(({ query: q }) => q === query)?.payload?.[searchOptions.getGoogle]
};


export const selectFindPeopleSearchResults = (state = {}, query) => { 
  if(!query) {
    return undefined
  }
  const cache = state?.search?.cache ?? []
  if(!cache?.length) {
    return undefined
  }
  
  return cache.find(({ query: q }) => q === query)?.payload?.[searchOptions.getPerson]
};

export const selectStatus = (state) => {
  const status =state.search.status
  return {
    status,
    isIdle: status === STATUSES.IDLE,
    isError: status === STATUSES.ERROR,
    isLoading: status === STATUSES.LOADING,
    isSuccess: status === STATUSES.SUCCESS,
  }
}

export const selectSearchResultMatchById = (state, options = {}) => {
  const { graphqlQuery, id, uuid, query } = options
  let resultsToPull = []

  if(graphqlQuery === getBuckeyeLinkData) {
    const categorizedResults = selectCategorizedResults(state)
    resultsToPull = categorizedResults
  }
  const matchInGeneric = searchResultOptions.find(o => o.query === graphqlQuery)  
  if(matchInGeneric) {
    const searchRes = selectQueryBasedSearchResults(state, query)
    resultsToPull = searchRes    
  }
  const primaryMatch = resultsToPull.find(({ uuid: reduxStoredUUOD }) => reduxStoredUUOD && uuid === reduxStoredUUOD)
  if(primaryMatch) {
    return primaryMatch
  }
  const secondaryMatch = resultsToPull.find(({ id: reduxId }) => reduxId && reduxId === id)
  if(secondaryMatch) {
    return secondaryMatch
  }

  return {}
}

export const selectSearchResultItems = (state, options = {}) => {
  const match = selectSearchResultMatchById(state, options)

  return match?.result?.items ?? []
}

export const selectRenderConditionForSection = (state, options = {}) => {
  const match = selectSearchResultMatchById(state, options)
  const itemLength = (match?.result?.items ?? []).length
  return match?.totalResults && itemLength
}

export const selectQueryBasedSearchResults = (state, query) => {
  return state?.search?.[query] ?? []
}

export const compareSearchResults = (a, b) => {
  return a?.query === b?.query
};
export default reducers;
